import React from "react";
import "../css/about.scss";
import { Link } from "react-router-dom";

function About() {
  const toTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <div className="about-page">
      {/* Breadcrumb */}
      <nav className="breadcrumb">
        <div className="container">
          <Link to="/" onClick={toTop}>
            Home
          </Link>
          <span className="separator">|</span>
          <span className="current">Our Story.</span>
        </div>
      </nav>

      <header className="page-header">
        <h1>Our Story.</h1>
      </header>

      {/* Hero Section */}
      <section className="hero-story">
        <div className="container">
          <div className="hero-grid">
            <div className="hero-image-wrapper">
              <img
                src="/main/a1.webp"
                alt="Tom & Annie"
                className="hero-image"
              />
              <div className="story-badge">
                <svg viewBox="0 0 100 100">
                  <path
                    id="circlePath"
                    d="M 50, 50 m -37, 0 a 37,37 0 1,1 74,0 a 37,37 0 1,1 -74,0"
                    fill="transparent"
                  />
                  <text>
                    <textPath xlinkHref="#circlePath">
                      TOM & ANNIE - OUR STORY -
                    </textPath>
                  </text>
                </svg>
              </div>
            </div>
            <div className="hero-content">
              <span className="subtitle">Meet Tom & Annie.</span>
              <h2>Never settling for mediocre metalwork.</h2>
              <div className="text-content">
                <p>
                  Pedarcircle began with a personal frustration. When renovating
                  our first home, the hardware never quite matched the care that
                  had gone into the rest of our projects.
                </p>
                <p>
                  Too generic or too flimsy. Too expensive or too fussy. Too
                  often the last decision made and the first one regretted.
                </p>
                <p>So we started making pieces that deserved to be there.</p>
                <p>
                  What began as a small edit of brass handles and knobs has
                  grown into a considered decorative hardware brand. We now work
                  closely with interior designers, architects and developers who
                  think about how a space actually comes together — not just how
                  it photographs.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="quote-section">
        <div className="container">
          <blockquote>
            "When creating our first home, Annie and I couldn't find hardware
            that reflected our style or budget – so I designed it."
          </blockquote>
          <cite>
            <span className="name">Tom Revill</span>
            <span className="title">Creative Director</span>
          </cite>
        </div>
      </section>

      {/* Decorative Details Section */}
      <section className="decorative-details">
        <div className="container">
          <div className="details-grid">
            <div className="details-image">
              <img src="/main/a2.webp" alt="Hardware detail" />
            </div>
            <div className="details-content">
              <h2>Decorative details for discerning spaces.</h2>
              <p className="tagline">
                It's not just a tagline — it's our filter.
              </p>
              <div className="definitions">
                <p>
                  <strong>Decorative,</strong> because hardware shouldn't
                  disappear into the background. It should add something.
                </p>
                <p>
                  <strong>Details,</strong> because these are the touchpoints —
                  the things people see, use and feel every day.
                </p>
                <p>
                  <strong>Discerning,</strong> because our customers know the
                  difference between really good and good enough.
                </p>
                <p>
                  <strong>Spaces,</strong> because nothing we design exists in
                  isolation — every piece is made to work as part of a bigger
                  picture.
                </p>
                <p>
                  In practice, that means collections that hold together across
                  a whole project — from cabinetry to switches and sockets —
                  with finishes and forms that sit in harmony, rather than
                  compete for attention.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="features-grid-section">
        <div className="container">
          <div className="features-grid">
            <div className="feature-item">
              <div className="feature-image">
                <img src="/main/a3-1.webp" alt="Designed in London" />
              </div>
              <div className="feature-text">
                <span className="number">1.</span>
                <h3>Designed in London</h3>
                <p>
                  From our London studio, our talented product meticulously
                  designs each hardware collection.
                </p>
              </div>
            </div>
            <div className="feature-item">
              <div className="feature-image">
                <img src="/main/a3-2.webp" alt="High Quality Materials" />
              </div>
              <div className="feature-text">
                <span className="number">2.</span>
                <h3>High Quality Materials</h3>
                <p>
                  We pride ourselves on specifying the best material for each
                  product, to ensure integrity and
                </p>
              </div>
            </div>
            <div className="feature-item">
              <div className="feature-image">
                <img src="/main/a3-3.webp" alt="B Corp Certified" />
              </div>
              <div className="feature-text">
                <span className="number">3.</span>
                <h3>B Corp Certified</h3>
                <p>
                  Since 2023, we've operated as a force for good in business
                  according to B Corp standards.
                </p>
              </div>
            </div>
            <div className="feature-item">
              <div className="feature-image">
                <img src="/main/a3-4.webp" alt="Trade Accounts" />
              </div>
              <div className="feature-text">
                <span className="number">4.</span>
                <h3>Trade Accounts</h3>
                <p>
                  We offer trade discounts and service benefits to repeat
                  professional customers.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Professionals Section */}
      <section className="professionals-section">
        <div className="container">
          <div className="prof-grid">
            <div className="prof-content">
              <h2>Designed with professionals in mind.</h2>
              <p className="intro">
                We design with trade expectations in mind.
              </p>
              <div className="prof-text">
                <p>
                  It means finishes that stay consistent across a whole project.
                  Products that are actually available when you need them.
                  Specifications written in plain English. Hardware that holds
                  up on site and still looks sharp once the dust has settled.
                </p>
                <p>
                  Whether you're working on a single residential project or a
                  multi-unit development, the idea is simple: fewer headaches at
                  the hardware stage, and a stronger result at the end of it.
                </p>
                <p className="strong">
                  No mismatched finishes. No last-minute compromises.
                </p>
              </div>
            </div>
            <div className="prof-image">
              <img src="/main/a4.webp" alt="Professional design" />
            </div>
          </div>
        </div>
      </section>

      {/* Details Obsessives Section */}
      <section className="obsessives-section">
        <div className="container">
          <div className="obs-grid">
            <div className="obs-image">
              <img src="/main/a5.webp" alt="Details obsessives" />
            </div>
            <div className="obs-content">
              <h2>For the details obsessives.</h2>
              <div className="obs-text">
                <p>
                  Pedarcircle exists for those who labour over the small stuff —
                  and for those whose clients do too.
                </p>
                <p>
                  The designers refining every line. The developers who know the
                  final 5% matters as much as the first 95%. The projects where
                  every decision has been made with the same level of care,
                  right to the end.
                </p>
                <p>
                  We're here for all of that — with hardware that's considered,
                  cohesive and made to complete a space properly.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Scrolling Banner */}
      <div className="scrolling-banner">
        <div className="banner-track">
          <span>Designed in London</span>
          <span>Trade Accounts</span>
          <span>Quality Materials</span>
          <span>Fast Delivery</span>
          <span>60 Days Free Returns</span>
          <span>Designed in London</span>
          <span>Trade Accounts</span>
          <span>Quality Materials</span>
          <span>Fast Delivery</span>
          <span>60 Days Free Returns</span>
        </div>
      </div>
    </div>
  );
}

export default About;
